import { Component, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { formatDistance } from 'date-fns';
import { IndividualService } from 'src/core/services/individual.service';
import { LocalStorageService } from 'src/core/services/local-storage.service';
import { PrescriptionService } from 'src/core/services/prescription.service';
import { Individual, IndividualType } from 'src/interfaces/individual';
import { UntypedFormBuilder, UntypedFormControl } from "@angular/forms";
import { format } from 'date-fns';

@Component({
    selector: 'core-individual-list',
    templateUrl: './individual-list.component.html',
    styleUrls: ['./individual-list.component.scss'],
    standalone: false
})
export class IndividualListComponent implements OnInit {

  displayedColumns: string[] = [
    'title',
    'name',
    'contact',
    'type',
    'sage',
    'created_at',
    'updated_at',
  ];

  allIndividualTypes = "";

  individualsDataSource: MatTableDataSource<Individual>;
  page: number = 1;
  limit: number = 10;
  totalNumberOfIndividuals: number = 0;
  individualTypes: IndividualType[] = [];
  individualType = new UntypedFormControl('');
  sortDirection: string = 'asc';
  orderBy: string = 'first_name';
  searchValue: string = '';
  individualFilterForm = this._formBuilder.group({
    individualTypeType: [''],
    search: [''],
  });

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private _individualService: IndividualService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _localStorageService: LocalStorageService,
    private _prescriptionService: PrescriptionService,
    private _formBuilder: UntypedFormBuilder
  ) {
    const limit = this._localStorageService.get('INDIVIDUALS', 'LIMIT');

    if (limit) {
      this.limit = parseInt(limit);
    }
  }

  ngOnInit(): void {

    this
      ._route
      .data
      .subscribe(data => {

        this.individualsDataSource = new MatTableDataSource<Individual>(data.individuals.individuals);
        this.page = data.individuals.pageNumber;
        this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();
        this.individualsDataSource.sort = this.sort;

        // Initialize filters from query params and fetch if present
        const qp = this._route.snapshot.queryParamMap;
        const qpSearch = qp.get('search');
        const qpIndividualType = qp.get('individualType');

        if (qpSearch) {
          this.individualFilterForm.controls['search'].setValue(qpSearch, { emitEvent: false });
          this.searchValue = qpSearch;
        }

        if (qpIndividualType) {
          this.individualType.setValue(qpIndividualType, { emitEvent: false });
        }

        if (qpSearch || qpIndividualType) {
          (async () => {
            this.page = 1;
            const newClients = await this._individualService
              .getIndividuals(this.page, this.limit, this.individualType.value, this.searchValue, null, this.sortDirection, this.orderBy);
            this.individualsDataSource.data = [...newClients];
            this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();
          })();
        }
      });


    this._prescriptionService
      .getFormData(['individual_type'])
      .subscribe(data => {
        this.individualTypes = data.individual_type;
      });


    this.individualType
      .valueChanges
      .subscribe(value => {

        (async () => {
          this.page = 1;
          const newClients = await this._individualService.getIndividuals(this.page, this.limit, value ? value : "","",null,this.sortDirection,this.orderBy);
          const newData = [
            ...newClients
          ]
          this.individualsDataSource.data = newData;
          this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();
        })();
      });

      this.individualFilterForm.controls['search'].valueChanges.subscribe(
        (value) => {
          this.page = 1;
          this.searchValue = value;
          (async () => {
            const newClients = await this._individualService
              .getIndividuals(this.page, this.limit, this.individualType.value,this.searchValue,null,this.sortDirection,this.orderBy);
            const newData = [
              ...newClients
            ]
            this.individualsDataSource.data = newData;
          })();
      
          this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();       
        }
      );

  }

  onPageChange(event: PageEvent) {

    // Get more orders 
    this.page = event.pageIndex + 1;
    this.limit = event.pageSize;

    this._localStorageService.store('INDIVIDUALS', 'LIMIT', this.limit.toString());
    this._localStorageService.store('INDIVIDUALS', 'PAGE', this.page.toString());
    
    (async () => {
      const newClients = await this._individualService
        .getIndividuals(this.page, this.limit, this.individualType.value,this.searchValue,null,this.sortDirection,this.orderBy);
      const newData = [
        ...newClients
      ]
      this.individualsDataSource.data = newData;
    })();

    this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();

  }


  getCreationTime(client: Individual) {
    // return formatDistance(new Date(client.created_at), new Date(), { addSuffix: true });
    return format(new Date(client.created_at), 'MMMM dd, yyyy HH:mm');
  }

  formatTime(time: string | null) {
    if (!time) return '-';
    return format(new Date(time), 'MMMM dd, yyyy HH:mm');
  }

  getIndividualType(individual: Individual) {

    if(!individual.individual_type_id) return "Not Applicable";
    
    const individualType: IndividualType | undefined = this.individualTypes
      .find(type => type.id === individual.individual_type_id);

    return individualType ? individualType.type : '-';
  }

  /** Same field as individual view (normalized on GET). */
  isOnSage(individual: Individual): boolean {
    return !!(
      individual?.sage_contact_id &&
      String(individual.sage_contact_id).trim()
    );
  }

  viewIndividual(id: number) {
    this._router.navigate([`/portal/individuals/view/${id}`], {
      queryParams: {
        search: this.individualFilterForm.value.search || undefined,
        individualType: this.individualType.value || undefined,
      },
    });
  }

  sortIndividuals(sort:Sort){
    switch (sort.active) {
      case 'name':
        this.orderBy = 'first_name';
        this.sortDirection = sort.direction;
        break;
      default:
        this.orderBy = 'first_name';
        this.sortDirection = 'asc';
        break;
    }
    this.page = 1;
    (async () => {
      const newClients = await this._individualService
        .getIndividuals(this.page, this.limit, this.individualType.value,this.searchValue,null,this.sortDirection,this.orderBy);
      const newData = [
        ...newClients
      ]
      this.individualsDataSource.data = newData;
    })();

    this.totalNumberOfIndividuals = this._individualService.getTotalNumberOfIndividuals();
  }

}
